import React, { useCallback, useMemo } from 'react';
import { getLocale, useIntl } from 'umi';
import { getBreakDownHeader } from "@/pages/dashboard/pages/engagement/_config/utils";
import { headerText } from '@/pages/dashboard/components/CountryRegionRowItem/constant';
import { renderCountryCell, renderValueCell, renderShareCell } from '@/pages/dashboard/components/CountryRegionRowItem/CountryTabBreakdown/renderUtils';
import { useSelectorMap } from '@/pages/dashboard/utils/customHook';
import { getMonetUnit } from '@/pages/dashboard/utils/moneyUnit';
import SvgIcon from '@/pages/home/components/SvgIcon';
import ACard from '@/pages/dashboard/components/ACard';
import CalculationBubble from '../CalculationBubble';
import { dauMetricsArr, formatRuleToTypeMap, shareAndTypeObj, titleAndTypeObj } from './_costant';
import {
  IUseMetricsConfigParams,
  IUseMetricsConfigReturn,
  ITableColumn,
  ITableRecord,
  IChartTooltipsConfig,
  IResolvedConfig,
} from './_type';
import { resolveConfigString, selectMetricNameByDateRange, resolveAndSelectName } from '@/pages/dashboard/utils/metricUtils';

/**
 * 处理指标配置的 Hook
 * 用于解析配置中的模板字符串，生成表格列配置和图表配置
 */
export const useMetricsConfig = (params: IUseMetricsConfigParams): IUseMetricsConfigReturn => {
  const {
    metricEnName,
    metricCnName,
    formatRule,
    changeFormatRule,
    shareFormatRule,
    metricsInfo,
    regionDataType,
    metric,
    isUserQuota,
    comConfig,
    jumpUrl,
    gameCode,
    haveCalBubble,
    progressList,
    platformByDataAllOs,
    defaultRegionDataType,
    optionsInit,
    jumpToLink,
    tabMsg,
    titleNameMsg,
    metricRangeEnName,
    metricRangeCnName,
    isSingle = true,
  } = params;

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isEN = locale === 'en-US';
  const { getFilterItemText } = useSelectorMap();

  // 解析配置中的模板字符串（仅在配置存在时解析）
  const resolvedConfig = useMemo((): IResolvedConfig | null => {
    // 如果没有任何配置，返回 null，使用原有逻辑
    if (
      !metricEnName &&
      !metricCnName &&
      !metricRangeEnName &&
      !metricRangeCnName &&
      !formatRule &&
      !changeFormatRule &&
      !shareFormatRule
    ) {
      return null;
    }

    return {
      metricEnName: resolveConfigString(metricEnName, metricsInfo),
      metricCnName: resolveConfigString(metricCnName, metricsInfo),
      metricRangeEnName: resolveConfigString(metricRangeEnName, metricsInfo),
      metricRangeCnName: resolveConfigString(metricRangeCnName, metricsInfo),
      formatRule: resolveConfigString(formatRule, metricsInfo),
      changeFormatRule: changeFormatRule || '',
      shareFormatRule: shareFormatRule || '',
    };
  }, [metricEnName, metricCnName, metricRangeEnName, metricRangeCnName, formatRule, changeFormatRule, shareFormatRule, metricsInfo]);

  // 生成表格列配置（仅在配置存在时生成，使用自定义 render）
  const tableColumns = useMemo((): ITableColumn[] | undefined => {
    // 如果没有配置，返回 undefined，使用原有逻辑
    if (!resolvedConfig) {
      return undefined;
    }

    const breakDownHeader = getBreakDownHeader(
      regionDataType,
      false,
      titleAndTypeObj?.[metric],
      dauMetricsArr?.includes(metric) ? 'Avg' : 'Sum',
      shareAndTypeObj?.[metric]
    );
    const headerData = (breakDownHeader as Record<string, { countryName: string; valueName: string; share: string; }>)?.[regionDataType] || {};
    const { countryName, valueName, share } = headerData;

    // 使用 headerText 来获取正确的翻译 key
    const countryHead = headerText[countryName] ? formatMessage({ id: headerText[countryName] }) : countryName;
    const defaultValueName = headerText[valueName] ? formatMessage({ id: headerText[valueName] }) : String(valueName);
    const displayMetricEnName = selectMetricNameByDateRange(
      isSingle,
      resolvedConfig.metricEnName,
      resolvedConfig.metricRangeEnName,
      defaultValueName
    );
    const displayMetricCnName = selectMetricNameByDateRange(
      isSingle,
      resolvedConfig.metricCnName,
      resolvedConfig.metricRangeCnName,
      defaultValueName
    );

    const valueHead = isEN ? displayMetricEnName : displayMetricCnName;

    const baseColumns: ITableColumn[] = [
      {
        key: 'country',
        header: countryHead,
        dataType: regionDataType,
        render: (record: ITableRecord) => renderCountryCell({ record, regionDataType, getFilterItemText }),
      },
      {
        key: 'value',
        header: valueHead,
        align: "right",
        dataType: 'dau',
        valueName: 'value',
        ratioField: 'change',
        render: (record: ITableRecord) => renderValueCell({
          record,
          formatRule: resolvedConfig.formatRule,
          changeFormatRule: resolvedConfig.changeFormatRule,
          isUserQuota,
        }),
      }
    ];

    // 如果有 shareFormatRule 且需要显示 share 列
    if (resolvedConfig.shareFormatRule && comConfig?.isHaveShare) {
      // 使用 headerText 和 share 来获取正确的翻译 key（与原有逻辑保持一致）
      const shareHead = headerText[share] ? formatMessage({ id: headerText[share] }) : share;
      baseColumns.push({
        key: 'share_of_all',
        align: "right",
        header: shareHead,
        dataType: 'twoDecimalPercent',
        render: (record: ITableRecord) => renderShareCell({
          record,
          shareFormatRule: resolvedConfig.shareFormatRule,
        }),
      });
    }

    return baseColumns;
  }, [regionDataType, metric, resolvedConfig, formatMessage, comConfig?.isHaveShare, isEN, isUserQuota, getFilterItemText, isSingle]);

  // 生成图表配置（仅在配置存在时生成）
  const chartTooltipsConfig = useMemo((): IChartTooltipsConfig | undefined => {
    const defaultConfig: IChartTooltipsConfig | undefined = metric === 'mtx_revenue_v4'
      ? {
        tooltipsName: `${formatMessage({ id: 'pc_overview_Revenue_v4' })}(${getMonetUnit()})`,
        tooltipsType: 'decimals' as const,
        tooltipsDecimal: 2,
      }
      : undefined;

    if (!resolvedConfig?.formatRule) {
      return defaultConfig;
    }

    // 从 formatRule 推断 tooltipsType
    let tooltipsType: 'int' | 'decimals' | 'percent' = 'decimals';
    const formatRule = resolvedConfig.formatRule.toLowerCase();
    
    if (formatRuleToTypeMap?.[resolvedConfig?.formatRule]) {
      tooltipsType = formatRuleToTypeMap[resolvedConfig.formatRule];
    } else if (formatRule.includes('percent')) {
      tooltipsType = 'percent';
    } else if (formatRule.includes('decimal')) {
      tooltipsType = 'decimals';
    } else if ((formatRule.includes('int') || formatRule.includes('thousands')) || formatRule.includes('bmk')) {
      tooltipsType = 'int';
    }

    // 提取小数位数
    let tooltipsDecimal = 2;
    if (tooltipsType === 'int') {
      tooltipsDecimal = 0;
    }
    
    return {
      tooltipsName: isEN ? resolvedConfig.metricEnName : (resolvedConfig.metricCnName || defaultConfig?.tooltipsName),
      tooltipsType,
      tooltipsDecimal,
    };
  }, [resolvedConfig, metric, isEN, formatMessage]);

  const renderTitleCard = useCallback((title: string, showProgressBubble: boolean): React.ReactNode => {
    const isClickable = jumpUrl && jumpUrl !== "none";

    return (
      <ACard url={isClickable ? `/dashboard/game/${gameCode}${jumpUrl}` : ''}>
        <div className='dbd-line-title-box'
          style={{ ...(isClickable ? {} : { pointerEvents: 'none' }) }}
        >
          <div
            className='dbd-line-title'
            onClick={isClickable ? jumpToLink : undefined}
          >
            {title}
          </div>
          {showProgressBubble && (
            <CalculationBubble
              tipList={progressList}
              isLoading={platformByDataAllOs?.loading}
            />
          )}
          {isClickable && (
            <span className='dbl-region-title-jump'>
              <SvgIcon className='dbd-line-title-icon' type='arrow-right'></SvgIcon>
            </span>
          )}
        </div>
      </ACard>
    );
  }, [jumpUrl, gameCode, jumpToLink, progressList, platformByDataAllOs?.loading]);


  // 生成 titleContent（支持配置的指标名称和 titleNameMsg）
  const titleContent = useMemo(() => {
    const showProgressBubble = !!(haveCalBubble && Array.isArray(progressList) && progressList.length > 0 && regionDataType === 'os');
    const titleConfig = titleNameMsg?.[regionDataType];
    // 优先使用 titleNameMsg 配置
    if (titleConfig) {
      const titleName = resolveAndSelectName(
        isEN,
        titleConfig.nameEn,
        titleConfig.nameCn,
        metricsInfo,
        tabMsg ? { tabMsg } : undefined
      );
      
      return {
        name: titleName,
        content: renderTitleCard(titleName, showProgressBubble),
      };
    }
    
    // 如果有配置的指标名称，使用指标名称
    if (resolvedConfig?.metricCnName || resolvedConfig?.metricEnName) {
      const titleName = resolveAndSelectName(
        isEN,
        resolvedConfig.metricEnName,
        resolvedConfig.metricCnName,
        metricsInfo
      );
      return {
        name: titleName,
        content: renderTitleCard(titleName, showProgressBubble),
      };
    }
    
    // 否则使用原有逻辑
    const titleMap: Record<string, Record<string, string>> = {
      dau: {
        os: formatMessage({ id: 'overview_monthly_breakdown_dau_platform' }),
        country: formatMessage({ id: 'overview_monthly_breakdown_dau_country' }),
        region: formatMessage({ id: 'overview_monthly_breakdown_dau_region' }),
      },
      mau: {
        os: formatMessage({ id: 'overview_monthly_breakdown_mau_platform' }),
        country: formatMessage({ id: 'overview_monthly_breakdown_mau_country' }),
        region: formatMessage({ id: 'overview_monthly_breakdown_mau_region' }),
      },
      wau: {
        os: formatMessage({ id: 'overview_monthly_breakdown_wau_platform' }),
        country: formatMessage({ id: 'overview_monthly_breakdown_wau_country' }),
        region: formatMessage({ id: 'overview_monthly_breakdown_wau_region' }),
      },
      mtx_revenue: {
        os: formatMessage({ id: 'overview_monthly_breakdown_mtx_platform' }),
        country: formatMessage({ id: 'overview_monthly_breakdown_mtx_country' }),
        region: formatMessage({ id: 'overview_monthly_breakdown_mtx_region' }),
      },
      new: {
        os: formatMessage({ id: 'overview_monthly_breakdown_new_platform' }),
        country: formatMessage({ id: 'overview_monthly_breakdown_new_country' }),
        region: formatMessage({ id: 'overview_monthly_breakdown_new_region' }),
        zone: formatMessage({ id: 'overview_monthly_breakdown_new_zone' }),
      },
      units: {
        os: formatMessage({ id: 'overview_monthly_breakdown_units_platform' }),
        country: formatMessage({ id: 'overview_monthly_breakdown_units_country' }),
        region: formatMessage({ id: 'overview_monthly_breakdown_units_region' }),
      },
      revenue: {
        os: formatMessage({ id: 'overview_monthly_breakdown_net_platform' }),
        country: formatMessage({ id: 'overview_monthly_breakdown_net_country' }),
        region: formatMessage({ id: 'overview_monthly_breakdown_net_region' }),
      },
      inflexion_new_churn: {
        os: formatMessage({ id: 'engagement_churn_rank_title_platform' }),
        country: formatMessage({ id: 'engagement_churn_rank_title_country' }),
        region: formatMessage({ id: 'engagement_churn_rank_title_region' }),
      },
      mtx_revenue_v4: {
        os: formatMessage({ id: 'overview_monthly_breakdown_mtx_v4_platform' }),
        country: formatMessage({ id: 'overview_monthly_breakdown_mtx_v4_country' }),
        region: formatMessage({ id: 'overview_monthly_breakdown_mtx_v4_region' }),
      },
      gross_full_game_units: {
        os: formatMessage({ id: 'gross_full_game_units_platform' }),
        country: formatMessage({ id: 'gross_full_game_units_country' }),
        region: formatMessage({ id: 'gross_full_game_units_region' }),
      },
      gross_revenue: {
        os: formatMessage({ id: 'gross_revenue_platform' }),
        country: formatMessage({ id: 'gross_revenue_country' }),
        region: formatMessage({ id: 'gross_revenue_region' }),
      }
    };
    // 指标切换类型
    if (defaultRegionDataType) {
      optionsInit?.forEach((item) => {
        const key = item?.value;
        const title = isEN ? item?.titleEn : item?.titleCn;
        titleMap[key] = {};
        titleMap[key][defaultRegionDataType] = title;
      });
    }

    return {
      name: titleMap?.[metric]?.[regionDataType] || '',
      content: renderTitleCard(titleMap?.[metric][regionDataType] || '', showProgressBubble),
    };
  }, [haveCalBubble, progressList, regionDataType, resolvedConfig?.metricCnName, resolvedConfig?.metricEnName, formatMessage, defaultRegionDataType, metric, renderTitleCard, isEN, optionsInit, titleNameMsg, metricsInfo, tabMsg]);

  return {
    resolvedConfig,
    tableColumns,
    chartTooltipsConfig,
    titleContent,
  };
};